import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminViews, isForbidden, workforce } from '@cwos/api-client';
import { Pill, SkeletonSet, SuccessEmpty, useFocusTrap, useTabListKeys } from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { authorityGate, gateOpen } from '../authorityGate.js';
import { GateNotice } from '../GateNotice.js';
import { ListState } from '../ListStates.js';
import { listPhase } from '../listState.js';
import { useNav } from '../navigation.js';
import { browserTimezone, toLocalDateString } from '../workforce/workforceLogic.js';
import { localDayString } from '../../lib/timeFormat.js';
import { SettingsHeader } from '../settings/common.js';
import {
  formatDistance,
  formatDuration,
  formatHours,
  overviewFrom,
  overviewScopeNote,
  splitVerification,
  verificationScopeNote,
  whosWorking,
  whosWorkingFromBoard,
  type WorkingRow,
} from './timeLogic.js';
import { LoadFailure } from '../LoadFailure.js';

/**
 * Time Clock — the product area that replaces the old standalone
 * "Check-in verification" screen.
 *
 * Verification was never the product; it was one report inside it. An
 * administrator asking "who is working, who should be, what did they record, and
 * what needs my decision" had nowhere to go, while a complete timekeeping,
 * timesheet and schedule API sat unused behind the UI. This area is that
 * missing surface.
 *
 * The tabs are separate ROUTES rather than local state, so a link to the
 * exceptions queue is a link someone can send.
 */

/**
 * Why this screen reads location, recorded with the audit event.
 *
 * `location.workforce_map_viewed` stores the reason a viewer gave. These are
 * fixed strings because the purpose really is fixed: an empty or vague reason on
 * a privacy read is worse evidence than an accurate constant one.
 */
const LOCATION_REASON_OVERVIEW = 'Time clock overview: checking today’s field signal';
const LOCATION_REASON_WORKING = 'Time clock: reviewing who is currently working';

export const TIME_TABS = [
  { key: 'overview', label: 'Overview', href: '/time' },
  { key: 'working', label: "Who's working", href: '/time/working' },
  { key: 'timesheets', label: 'Timesheets', href: '/time/timesheets' },
  { key: 'exceptions', label: 'Exceptions', href: '/time/exceptions' },
  { key: 'approvals', label: 'Approvals', href: '/time/approvals' },
] as const;

export type TimeTabKey = (typeof TIME_TABS)[number]['key'];

export function TimeClockLayout({ active, children }: { active: TimeTabKey; children: ReactNode }) {
  const navigate = useNav();
  const sectionTabs = useTabListKeys<HTMLDivElement>();
  return (
    <div className="rn-col" style={{ maxWidth: 1180 }}>
      <SettingsHeader
        title="Time clock"
        blurb="Who is working, what they recorded, and what needs a decision."
      />
      {/* `aria-pressed` dropped (invalid on `role="tab"`). These tabs navigate
          to a route, so the panel is whatever the router renders below them —
          which is exactly what `aria-controls` now names. */}
      <div className="rn-tabs" role="tablist" aria-label="Time clock sections" {...sectionTabs}>
        {TIME_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            id={`time-tab-${tab.key}`}
            aria-controls="time-panel"
            className="rn-tab"
            aria-selected={active === tab.key}
            onClick={() => navigate(tab.href)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div id="time-panel" role="tabpanel" aria-labelledby={`time-tab-${active}`}>
        {children}
      </div>
    </div>
  );
}

/**
 * The word that goes with a tone.
 *
 * A coloured dot alone is not a status: it fails for anyone who cannot
 * distinguish the colours, and it fails in a screenshot pasted into a message.
 */
const TONE_WORD: Record<'good' | 'look' | 'urgent' | 'unsure', string> = {
  good: 'Clear',
  look: 'Check',
  urgent: 'Act',
  unsure: 'Unknown',
};

/** A metric that is also a link. Every number on this surface drills somewhere. */
export function DrillStat({
  label,
  value,
  hint,
  tone,
  onOpen,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'good' | 'look' | 'urgent' | 'unsure' | 'plain';
  onOpen?: () => void;
}) {
  const body = (
    <>
      <span className="db-kpi-label">{label}</span>
      <span className="db-kpi-value">{value}</span>
      <span className="db-kpi-foot">
        {hint ? <span className="rn-muted">{hint}</span> : <span />}
        {tone && tone !== 'plain' ? (
          <Pill variant={tone} dot>
            {TONE_WORD[tone]}
          </Pill>
        ) : null}
      </span>
    </>
  );
  if (!onOpen) return <div className="db-kpi">{body}</div>;
  return (
    <button type="button" className="db-kpi tc-kpi-btn" onClick={onOpen}>
      {body}
    </button>
  );
}

// ============================================================== OVERVIEW ====

/**
 * Time Clock Overview.
 *
 * Answers the 10-second question — what needs my attention — with counts that
 * are each the length of a list this app can actually show. Nothing here is a
 * card that cannot be opened.
 */
export function TimeOverview() {
  const navigate = useNav();
  const { has } = usePermissions();
  const now = useMemo(() => new Date(), []);
  // "Today" as the reader's own calendar day — `toISOString().slice(0,10)` is
  // the UTC date, which rolls to tomorrow around 19:00 local and left the
  // "today" map empty every evening while the KPI beside it still said
  // "Recorded today".
  const today = localDayString(now);

  const board = useQuery({
    queryKey: ['admin-views', 'workforce'],
    queryFn: () => adminViews.workforce(),
    refetchInterval: 60_000,
  });

  // The map is the only org-wide source of open sessions WITH location; without
  // the location permission the counts still work, they just lose freshness.
  const canSeeLocation = has('location_history:view');
  const map = useQuery({
    queryKey: ['workforce', 'map', today],
    // Reading the map is an audited privacy read, and the reason is recorded
    // with it. It is stated here rather than prompted for because this screen
    // has exactly one purpose and a free-text box would only invite a blank.
    queryFn: () =>
      workforce.workforceMap(today, { tz: browserTimezone(), reason: LOCATION_REASON_OVERVIEW }),
    enabled: canSeeLocation,
  });

  const verification = useQuery({
    queryKey: ['admin-views', 'verification'],
    queryFn: () => adminViews.verification(),
  });

  const working = useMemo(
    () => (map.data ? whosWorking(map.data, now) : whosWorkingFromBoard(board.data, now)),
    [map.data, board.data, now],
  );
  const overview = overviewFrom(board.data, working);
  const split = splitVerification(verification.data);
  /** The reader holds the permission and the map still has not answered. */
  const mapUnread = canSeeLocation && map.data === undefined;
  /** Both cards below count open sessions; with no clock there are none to count. */
  const noClock = board.data?.hasTimeClock === false;

  if (board.isLoading) return <SkeletonSet surface="table" rows={6} />;
  // A 403 is not a failure to retry — it is an answer. Reading the whole
  // organization's clock needs `admin-views:read`, which a worker does not hold,
  // and offering them a Retry button would be a lie with a button on it.
  if (isForbidden(board.error)) return <OrgWideDenied />;
  if (board.isError && !board.data) {
    return (
      <LoadFailure
        error={board.error}
        message="We couldn’t load the time clock just now."
        onRetry={() => void board.refetch()}
      />
    );
  }

  return (
    <div className="rn-col">
      <section className="rn-card">
        <h2 className="rn-card-title">Right now</h2>
        {/* The frame for the four counts below, present only when they are
            narrower than the organisation. See `overviewScopeNote`. */}
        {overviewScopeNote(board.data) ? (
          <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
            {overviewScopeNote(board.data)}
          </p>
        ) : null}
        {noClock ? (
          // NO TIME CLOCK, NO CLOCK FIGURES (owner ruling 2026-10-07): four
          // zeros under a green "None open" would be an all-clear measured
          // against a source this organization never connected.
          <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
            No time clock connected — this organization does not record clock-ins in Phibian, so
            clock-ins, missing clock-outs and hours on the clock are not measured.
          </p>
        ) : (
          <div className="tc-kpis">
            <DrillStat
              label="Clocked in"
              value={String(overview.clockedIn)}
              hint="Open sessions"
              onOpen={() => navigate('/time/working')}
            />
            <DrillStat
              label="Missing clock-out"
              value={String(overview.missingClockOut)}
              hint={overview.missingClockOut > 0 ? 'Hours unknown, not zero' : 'None open'}
              tone={overview.missingClockOut > 0 ? 'urgent' : 'good'}
              onOpen={() => navigate('/time/exceptions')}
            />
            <DrillStat
              label="Scheduled, not clocked in"
              value={String(overview.notClockedIn)}
              hint="Shift started, no session"
              tone={overview.notClockedIn > 0 ? 'look' : 'good'}
              onOpen={() => navigate('/time/exceptions')}
            />
            <DrillStat
              label="Recorded today"
              value={formatHours(overview.totalMinutesToday)}
              hint="Across open sessions"
              onOpen={() => navigate('/time/timesheets')}
            />
          </div>
        )}
      </section>

      {canSeeLocation ? (
        <section className="rn-card">
          <h2 className="rn-card-title">Field signal</h2>
          <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
            Phibian reports the last location a worker’s device sent. It does not track anyone
            continuously, and nothing here means a worker is currently at a place.
          </p>
          {/*
            THE MAP READ HAS TO BE SURFACED, because this card is computed from
            it and was reporting its failure as a finding.

            `working` falls back to `whosWorkingFromBoard` whenever `map.data`
            is undefined, and that builder assigns every row
            `locationFreshness(null)` — level `none`. `staleLocations` counts
            `stale` plus `none`. So a 500 or a 403 on the map rendered
            "Stale or absent location: <everyone>" under a "Check" pill, to the
            only readers who hold the permission and would act on it. The count
            was a fact about the request, presented as a fact about the field.
          */}
          {mapUnread ? (
            <p className="rn-muted" role="status" style={{ marginTop: 'var(--space-8)' }}>
              {map.isError
                ? 'Positions could not be read just now, so location freshness is unknown rather than stale.'
                : 'Positions have not been read yet.'}
            </p>
          ) : null}
          {noClock ? null : (
            <div className="tc-kpis" style={{ marginTop: 'var(--space-12)' }}>
              <DrillStat
                label="Long open sessions"
                value={String(overview.longShifts)}
                hint="9 hours or more"
                tone={overview.longShifts > 0 ? 'look' : 'good'}
                onOpen={() => navigate('/time/working')}
              />
              <DrillStat
                label="Stale or absent location"
                value={mapUnread ? '—' : String(overview.staleLocations)}
                hint={mapUnread ? 'Not read' : 'No recent position'}
                tone={mapUnread ? 'plain' : overview.staleLocations > 0 ? 'unsure' : 'good'}
                onOpen={() => navigate('/field/map')}
              />
            </div>
          )}
        </section>
      ) : null}

      <section className="rn-card">
        <div className="rn-card-head">
          <h2 className="rn-card-title">Check-in verification</h2>
          <button
            type="button"
            className="rn-btn-quiet"
            onClick={() => navigate('/time/exceptions')}
          >
            Open the queue
          </button>
        </div>
        {/* Whose check-ins these are, present only when narrower than the
            organisation. See `verificationScopeNote`. */}
        {verificationScopeNote(verification.data) ? (
          <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
            {verificationScopeNote(verification.data)}
          </p>
        ) : null}
        {/* `splitVerification(undefined)` totals 0, so a failed verification
            read used to report "Nothing recorded yet" — i.e. that no check-ins
            exist — on a card whose whole job is to say how many did. */}
        <ListState
          phase={listPhase(verification, split.total)}
          rows={3}
          onRetry={() => void verification.refetch()}
          failedMessage="We couldn’t summarize check-in verification just now. No check-ins have been lost; we simply could not read them."
          empty={
            <SuccessEmpty
              heading="Nothing recorded yet"
              message="No check-ins have been recorded to summarize."
            />
          }
        >
          {/* THE TONES ON THIS ROW, AND WHY "At a work site" NO LONGER CARRIES
              ONE. It was hard-coded `tone="good"`, which renders the word
              "Clear" beside the number regardless of what the number is — so a
              day on which ZERO check-ins were verified inside a geofence read
              "0 · Clear". A count is not a verdict, and the verdict on this
              card is `needsLook`, which has one. Same reasoning as
              `ADR 0017 §8` applies to EVV: a green tick beside a figure is read
              as "this is fine", and it must be earned by the figure that
              actually says so.

              `unknown` is rendered whenever it is non-zero for a different
              reason: `splitVerification` computes it and this card used to drop
              it, so a punch in a state the client does not recognise vanished
              from both buckets while still counting in `total` — three numbers
              that visibly failed to add up, with nothing saying why. */}
          <div className="tc-kpis" style={{ marginTop: 'var(--space-12)' }}>
            <DrillStat label="Check-ins" value={String(split.total)} hint="Recent punches" />
            <DrillStat
              label="At a work site"
              value={String(split.atSite)}
              hint="Verified inside a geofence"
              onOpen={() => navigate('/time/exceptions?bucket=at_site')}
            />
            <DrillStat
              label="Needs a look"
              value={String(split.needsLook)}
              hint="Outside, unverified or unavailable"
              tone={split.needsLook > 0 ? 'look' : 'good'}
              onOpen={() => navigate('/time/exceptions?bucket=needs_look')}
            />
            {split.unknown > 0 ? (
              <DrillStat
                label="Not recognized"
                value={String(split.unknown)}
                hint="A verification state this screen does not know"
                tone="unsure"
              />
            ) : null}
          </div>
        </ListState>
      </section>
    </div>
  );
}

/**
 * What someone without organization-wide time access is told.
 *
 * Named plainly and pointed somewhere useful: a worker's own time is on their
 * profile, which they can reach.
 */
function OrgWideDenied() {
  const navigate = useNav();
  return (
    <section className="rn-card">
      <h2 className="rn-card-title">You can’t see the organization’s time clock</h2>
      <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
        Reading everyone’s sessions needs an organization-wide permission that supervisors and
        administrators hold. Your own recorded time is on your profile.
      </p>
      <div className="tc-drawer-actions">
        <button type="button" className="rn-btn-quiet" onClick={() => navigate('/me/profile')}>
          Open your profile
        </button>
      </div>
    </section>
  );
}

// ========================================================== WHO'S WORKING ===

/**
 * Who's working.
 *
 * One row per OPEN session, ordered longest-running first. The location column
 * states when a position was received, never that a worker is somewhere — the
 * distinction the whole location posture rests on.
 */
export function WhosWorking() {
  const navigate = useNav();
  const permissions = usePermissions();
  const [selected, setSelected] = useState<WorkingRow | null>(null);
  const now = useMemo(() => new Date(), []);
  const today = localDayString(now);
  /**
   * FOUR ANSWERS, NOT TWO — and this is the site where the defect is HARDEST TO
   * SEE, which is why it is worth writing down.
   *
   * `location_history:view` is granted to no role by default, so the note below
   * ("Location columns are hidden: … needs the location-history permission")
   * renders for almost everybody and is right for almost everybody. That is
   * exactly what hides the case where it is wrong: a holder of the key whose
   * `/auth/me` could not be re-read is told their location columns are hidden
   * for want of a permission they hold. This route is gated on
   * `ADMIN_VIEWS_READ`, a different key, so a cached set carrying that key
   * walks the reader through `RequirePermission` and into this note.
   *
   * `canSeeLocation` stays default-deny for the map query's `enabled:`, the
   * "Open the live map" link and the board-only fallback. None of those says
   * anything to anybody.
   */
  const locationGate = authorityGate(permissions, 'location_history:view');
  const canSeeLocation = gateOpen(locationGate);

  const board = useQuery({
    queryKey: ['admin-views', 'workforce'],
    queryFn: () => adminViews.workforce(),
    refetchInterval: 60_000,
  });
  const map = useQuery({
    queryKey: ['workforce', 'map', today],
    queryFn: () =>
      workforce.workforceMap(today, { tz: browserTimezone(), reason: LOCATION_REASON_WORKING }),
    enabled: canSeeLocation,
    refetchInterval: 60_000,
  });

  // Without the location permission the map is unavailable, but the board still
  // knows who is clocked in. Both sources produce the SAME row type, so the
  // screen has one rendering path and one interaction either way — only the
  // location fields differ, and they say so.
  const usingBoardOnly = !canSeeLocation || (!map.data && !map.isLoading);
  const rows = useMemo(
    () => (usingBoardOnly ? whosWorkingFromBoard(board.data, now) : whosWorking(map.data, now)),
    [usingBoardOnly, board.data, map.data, now],
  );

  if (board.isLoading || (canSeeLocation && map.isLoading)) {
    return <SkeletonSet surface="table" rows={6} />;
  }
  if (isForbidden(board.error)) return <OrgWideDenied />;
  if (board.isError && !board.data) {
    return (
      <LoadFailure
        error={board.error}
        message="We couldn’t load who is working just now."
        onRetry={() => void board.refetch()}
      />
    );
  }

  const count = rows.length;

  return (
    <div className="rn-col">
      <section className="rn-card">
        <div className="rn-card-head">
          <h2 className="rn-card-title">
            {count} {count === 1 ? 'person' : 'people'} clocked in
          </h2>
          {canSeeLocation ? (
            <button type="button" className="rn-btn-quiet" onClick={() => navigate('/field/map')}>
              Open the live map
            </button>
          ) : null}
        </div>

        <GateNotice
          gate={locationGate}
          what="see where a worker was"
          onRetry={() => permissions.refetch()}
          refusedNode={
            <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
              Location columns are hidden: viewing where a worker was needs the location-history
              permission, which is granted to no role by default.
            </p>
          }
        />

        {count === 0 ? (
          <SuccessEmpty
            heading="Nobody is clocked in"
            message="When someone starts a shift they will appear here with their elapsed time."
          />
        ) : (
          <ul className="tc-rows">
            {rows.map((row) => (
              <li key={row.workerId} className="tc-row">
                <button
                  type="button"
                  className="tc-row-btn"
                  onClick={() => setSelected(row)}
                  aria-label={`${row.name}, ${formatDuration(row.elapsedMinutes)} elapsed. Open details.`}
                >
                  <span className="tc-row-main">
                    <span className="tc-row-name">{row.name}</span>
                    <span className="rn-muted">
                      {!row.detailsKnown
                        ? 'Role and program not carried by this view'
                        : [row.roleTitle, row.programName].filter(Boolean).join(' · ') ||
                          'No program recorded'}
                    </span>
                    <span className="rn-muted">{row.freshness.label}</span>
                  </span>
                  <span className="tc-row-side">
                    <span className="tc-elapsed">{formatDuration(row.elapsedMinutes)}</span>
                    {row.shiftLength.label ? (
                      <Pill variant={row.shiftLength.level === 'overlong' ? 'urgent' : 'look'} dot>
                        {row.shiftLength.level === 'overlong' ? 'Check clock-out' : 'Long shift'}
                      </Pill>
                    ) : null}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {selected ? <WorkerNowPanel row={selected} onClose={() => setSelected(null)} /> : null}
    </div>
  );
}

/**
 * The contextual panel for one working person.
 *
 * Opens beside the list rather than navigating away, because the question
 * "what's going on with this one worker" is usually asked while scanning the
 * rest of them.
 */
function WorkerNowPanel({ row, onClose }: { row: WorkingRow; onClose: () => void }) {
  const navigate = useNav();
  const { has } = usePermissions();
  // The link is offered only to somebody who can actually follow it. The server
  // refuses either way; a dead-ended button is just a worse way to find out.
  const canSeeLocation = has('location_history:view');
  const today = toLocalDateString(new Date());
  // Same as the audit drawer: `aria-modal` without Tab containment is a claim
  // the markup does not keep.
  const ref = useFocusTrap<HTMLElement>(true, { onEscape: onClose });

  return (
    <aside
      ref={ref}
      className="cmp-drawer"
      role="dialog"
      aria-modal="true"
      aria-label={`${row.name} right now`}
    >
      <div className="cmp-drawer-head">
        <h2 className="rn-card-title">{row.name}</h2>
        <button type="button" className="rn-btn-quiet" onClick={onClose}>
          Close
        </button>
      </div>

      <dl className="cmp-facts">
        <div>
          <dt>Role</dt>
          <dd>{!row.detailsKnown ? 'Not read on this view' : (row.roleTitle ?? 'Not recorded')}</dd>
        </div>
        <div>
          <dt>Program</dt>
          <dd>
            {!row.detailsKnown ? 'Not read on this view' : (row.programName ?? 'Not recorded')}
          </dd>
        </div>
        <div>
          <dt>Clocked in</dt>
          <dd>
            {row.clockedInAt
              ? new Date(row.clockedInAt).toLocaleTimeString(undefined, {
                  hour: 'numeric',
                  minute: '2-digit',
                })
              : '—'}
          </dd>
        </div>
        <div>
          <dt>Elapsed</dt>
          <dd>{formatDuration(row.elapsedMinutes)}</dd>
        </div>
        <div>
          <dt>Location</dt>
          {/* Past tense, always. This is the last thing a device sent, not a
              claim about where somebody is standing. */}
          <dd>
            {row.freshness.label}
            {row.siteName ? <span className="rn-muted"> — near {row.siteName}</span> : null}
          </dd>
        </div>
      </dl>

      {row.shiftLength.label ? (
        <p className="rn-muted" style={{ marginTop: 'var(--space-12)' }}>
          {row.shiftLength.label}
        </p>
      ) : null}

      <div className="tc-drawer-actions">
        {row.sessionId ? (
          <button
            type="button"
            className="rn-btn-quiet"
            onClick={() => navigate(`/sessions/${row.sessionId}`)}
          >
            Open this session
          </button>
        ) : null}
        {/* The route the owner could not find: from the person in front of you
            to where they have been today. It was previously reachable only by
            opening the map from the rail and picking the date and the name
            again — assuming the rail item was there at all. */}
        {canSeeLocation ? (
          <button
            type="button"
            className="rn-btn-quiet"
            onClick={() =>
              navigate(
                `/field/map?date=${encodeURIComponent(today)}&worker=${encodeURIComponent(row.workerId)}` +
                  (row.sessionId ? `&session=${encodeURIComponent(row.sessionId)}` : ''),
              )
            }
          >
            Where they have been today
          </button>
        ) : null}
        <button
          type="button"
          className="rn-btn-quiet"
          onClick={() => navigate(`/people/${row.workerId}`)}
        >
          Open the worker
        </button>
      </div>
    </aside>
  );
}

export { formatDistance };
